// Promotor (fase 4). Contrato: docs/CONTRATO-API-FASE4.md.
//
// Dos formas de entrar al mismo panel `/api/promoter/*`:
//   mode 'persona' : promotor independiente, token `promoter-token` (aparte de
//                    empresa, usuario, staff y admin; nunca se mezcla).
//   mode 'empresa' : una empresa con perfil de promotor usa su token de empresa
//                    (`empresa-token`), sin segundo login.
import { ApiError, NETWORK_ERROR_MESSAGE } from './staffApi';
import { query } from '../utils/query';

export { ApiError };

export const PROMOTER_TOKEN_KEY = 'promoter-token';

export const SESSION_INVALID_MESSAGE = 'Tu sesión ya no es válida. Inicia sesión de nuevo.';
export const SESSION_EXPIRED_MESSAGE = 'Tu sesión venció. Inicia sesión de nuevo para continuar.';

export const getPromoterToken = () => {
  try {
    return localStorage.getItem(PROMOTER_TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

export const setPromoterToken = (token) => {
  try {
    localStorage.setItem(PROMOTER_TOKEN_KEY, token);
  } catch (e) {
    // sin localStorage la sesión dura lo que dure la pestaña (no se guarda)
  }
};

export const clearPromoterToken = () => {
  try {
    localStorage.removeItem(PROMOTER_TOKEN_KEY);
  } catch (e) {
    // nada que limpiar
  }
};

// La sesión de promotor ya no sirve (`promoter_session_invalid` o JWT vencido):
// el token ya se borró y hay que mandar al login.
export class PromoterSessionError extends ApiError {
  constructor(message = SESSION_INVALID_MESSAGE, opts = {}) {
    super(message, opts);
    this.name = 'PromoterSessionError';
  }
}

// El promotor no está aprobado: `promoter_pending` | `promoter_rejected` |
// `promoter_suspended`. `promoterStatus` = 'pending' | 'rejected' | 'suspended'.
export class PromoterStatusError extends ApiError {
  constructor(message, opts = {}) {
    super(message, opts);
    this.name = 'PromoterStatusError';
    this.promoterStatus = (opts.data && opts.data.status) || String(opts.code || '').replace('promoter_', '');
  }
}

const STATUS_CODES = ['promoter_pending', 'promoter_rejected', 'promoter_suspended'];

export const NOT_A_PROMOTER = 'not_a_promoter';

const readJson = async (response) => {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

const tokenFor = (mode) => {
  try {
    return localStorage.getItem(mode === 'empresa' ? 'empresa-token' : PROMOTER_TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

// Petición con el token que toca. Lanza PromoterSessionError (solo `persona`),
// PromoterStatusError (403 por estado) o ApiError.
export const promoterFetch = async (mode, path, { method = 'GET', body } = {}) => {
  const headers = { Authorization: `Bearer ${tokenFor(mode)}` };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError(NETWORK_ERROR_MESSAGE);
  }

  const data = await readJson(response);

  if (response.status === 401 && mode !== 'empresa') {
    // `promoter_session_invalid` o JWT vencido ({"msg": "Token has expired"}):
    // en los dos casos el login es la única salida.
    clearPromoterToken();
    const expired = Boolean(data && !data.code && typeof data.msg === 'string' && /expired/i.test(data.msg));
    throw new PromoterSessionError(
      expired ? SESSION_EXPIRED_MESSAGE : SESSION_INVALID_MESSAGE,
      { status: 401, code: data && data.code, data }
    );
  }

  if (response.status === 403 && data && STATUS_CODES.includes(data.code)) {
    throw new PromoterStatusError(
      data.error || 'Tu cuenta de promotor no puede operar por ahora.',
      { status: 403, code: data.code, data }
    );
  }

  if (!response.ok || !data || data.success === false) {
    throw new ApiError(
      (data && data.error) || 'Ocurrió un error. Inténtalo de nuevo.',
      { status: response.status, code: data && data.code, data }
    );
  }
  return data;
};

// --- autenticación del promotor persona (sin token) ----------------------

const publicPost = async (path, payload, { rateLimitMessage, fallback }) => {
  let response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    throw new ApiError(NETWORK_ERROR_MESSAGE);
  }
  const data = await readJson(response);
  if (response.status === 429) {
    throw new ApiError(rateLimitMessage, { status: 429 });
  }
  if (!response.ok || !data || !data.success) {
    throw new ApiError(
      (data && data.error) || fallback,
      { status: response.status, code: data && data.code, data }
    );
  }
  return data;
};

// payload: { name, email, password, phone?, city? } -> { token, promoter, message }
export const promoterRegister = (payload) =>
  publicPost('/api/auth/promoter/register', payload, {
    rateLimitMessage: 'Demasiados registros desde esta conexión. Espera un momento e inténtalo de nuevo.',
    fallback: 'No se pudo crear la cuenta. Inténtalo de nuevo.',
  });

// -> { token, promoter } (también si está pendiente/rechazado/suspendido)
export const promoterLogin = (email, password) =>
  publicPost('/api/auth/promoter/login', { email, password }, {
    rateLimitMessage: 'Demasiados intentos. Espera un minuto e inténtalo de nuevo.',
    fallback: 'No se pudo iniciar sesión. Inténtalo de nuevo.',
  });

// --- panel del promotor (persona o empresa) --------------------------------

// -> { promoter, links: {pending, accepted, rejected, revoked}, canOperate }
export const fetchPromoterMe = (mode) => promoterFetch(mode, '/api/promoter/me');

// Solo empresa dueña. 201 { promoter: {status: 'pending'} } · 409 si ya tiene.
export const requestEmpresaPromoterProfile = () =>
  promoterFetch('empresa', '/api/empresa/promoter-profile', { method: 'POST' });

// params: { search, page, limit }
export const listPromoterEmpresas = (mode, params) =>
  promoterFetch(mode, `/api/promoter/empresas${query(params)}`);

// status: all | pending | accepted | rejected | revoked
export const listPromoterLinks = (mode, status = 'all') =>
  promoterFetch(mode, `/api/promoter/links${query({ status })}`);

export const requestPromoterLink = (mode, empresaId, message) =>
  promoterFetch(mode, '/api/promoter/links', {
    method: 'POST',
    body: message ? { empresaId, message } : { empresaId },
  });

// Retira una solicitud pendiente o termina una relación aceptada.
export const endPromoterLink = (mode, id) =>
  promoterFetch(mode, `/api/promoter/links/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const listPromoterActivities = (mode, empresaId) =>
  promoterFetch(mode, `/api/promoter/activities${query({ empresaId })}`);

// params: { from, to, status, empresaId, source, page, limit }
export const listPromoterSales = (mode, params) =>
  promoterFetch(mode, `/api/promoter/sales${query(params)}`);

// params: { status, empresaId, from, to, page, limit }
export const listPromoterCuts = (mode, params) =>
  promoterFetch(mode, `/api/promoter/cuts${query(params)}`);

export const getPromoterCut = (mode, id) =>
  promoterFetch(mode, `/api/promoter/cuts/${encodeURIComponent(id)}`);
